import { ContractError } from './contracts';

export type CoinTransaction = Readonly<{
  id: number;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  source: string;
  description: string;
  createdAt: string;
}>;

export type CoinTransactionPage = Readonly<{
  transactions: readonly CoinTransaction[];
  sources: readonly string[];
  hasMore: boolean;
  nextBeforeId: number;
}>;

export type CoinTransactionGateway = (
  source: string,
  beforeId: number,
  signal: AbortSignal,
) => Promise<CoinTransactionPage>;

export function parseCoinTransactions(value: unknown): CoinTransactionPage {
  const invalid = (): never => {
    throw new ContractError('Invalid coin transaction response.');
  };
  const object = (value: unknown): Record<string, unknown> => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return invalid();
    return value as Record<string, unknown>;
  };
  const integer = (value: unknown, minimum = 0): number => {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < minimum)
      return invalid();
    return value;
  };
  const text = (value: unknown): string => (typeof value === 'string' ? value : invalid());
  const body = object(value);
  if (
    body.protocolVersion !== '2' ||
    !Array.isArray(body.transactions) ||
    body.transactions.length > 100 ||
    !Array.isArray(body.sources) ||
    typeof body.hasMore !== 'boolean'
  )
    return invalid();
  const transactions = body.transactions.map((value): CoinTransaction => {
    const row = object(value);
    const createdAt = text(row.createdAt);
    if (!Number.isFinite(Date.parse(createdAt))) return invalid();
    return {
      id: integer(row.id, 1),
      amount: integer(row.amount, Number.MIN_SAFE_INTEGER),
      balanceBefore: integer(row.balanceBefore),
      balanceAfter: integer(row.balanceAfter),
      source: text(row.source),
      description: text(row.description),
      createdAt,
    };
  });
  const nextBeforeId = integer(body.nextBeforeId);
  if (body.hasMore && (transactions.length === 0 || nextBeforeId !== transactions.at(-1)?.id))
    return invalid();
  return { transactions, sources: body.sources.map(text), hasMore: body.hasMore, nextBeforeId };
}

export function coinEventLabel(source: string): string {
  const labels: Readonly<Record<string, string>> = {
    tebex_purchase: 'Coin purchase',
    tebex_gift_bonus: 'Coin gift bonus',
    nordrevip_purchase: 'NordReVIP purchase bonus',
    weekly_payout: 'Weekly payout',
    admin_adjustment: 'Administrator adjustment',
    legacy_redemption: 'Legacy reward',
    promo_code: 'Promo code',
    tombola_reward: 'Level reward',
    referral_reward: 'Referral reward',
    treasure: 'Coin treasure',
    companion_purchase: 'Pet, mount or Nobo purchase',
    ockelboda_market_order: 'Market order',
    ockelboda_market_trade: 'Market trade',
    ockelboda_market_refund: 'Market refund',
  };
  return (
    labels[source] ?? source.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())
  );
}
